import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BaseTask, GenerateBaseTaskForm, User } from '@ee/common/models';
import { CaseTaskOperation, TaskType } from '@ee/common/enums';
import { Subscription } from 'rxjs';
import { CaseTaskEditorType } from '@ee/common/enums';
import { actorName } from '@ee/common/utils';

@Component({
  selector: 'ee-case-task-editor-dialog',
  template: `
    <h2 mat-dialog-title>{{ title }}</h2>
    <form mat-dialog-content class="flex flex-col justify-stretch" [formGroup]="form" (ngSubmit)="save()">
      <ee-task-form
        [taskForm]="form"
        [dynamicDescriptionHeight]="true"
        [userSuggestions]="data.userSuggestions"
        [hideTaskScheduler]="data.editorType === CaseTaskEditorType.WorkflowEdit"></ee-task-form>

      @if (data.type === TaskType.CASE_STEP_TASK) {
        <div class="mb-4 flex flex-row justify-start items-center">
          <mat-checkbox formControlName="required">Required</mat-checkbox>
          <mat-icon
            class="ml-1 mr-4 cursor-pointer"
            fontSet="fa"
            fontIcon="fa-info-circle"
            tp="The completion of the workflow step is blocked until this task has been completed."></mat-icon>
          <mat-checkbox formControlName="remove_on_step_completion">Remove on step completion</mat-checkbox>
          <mat-icon
            class="ml-1 cursor-pointer"
            fontSet="fa"
            fontIcon="fa-info-circle"
            tp="When enabled, this task will be removed if it is unfinished when the workflow step is processed."></mat-icon>
        </div>
      }

      @if (data.taskDetails?.update_history?.length) {
        <div class="update-history-wrapper">
          <div class="update-history-header">
            <div class="update-history-title">Update History</div>
            <button
              class="ee__button ee__chevron-button"
              mat-icon-button
              type="button"
              (click)="showUpdateHistory = !showUpdateHistory">
              <mat-icon fontSet="fa" [fontIcon]="showUpdateHistory ? 'fa-chevron-down' : 'fa-chevron-up'"></mat-icon>
            </button>
          </div>
          @if (showUpdateHistory) {
            <div class="update-history">
              @for (history of data.taskDetails.update_history; track history) {
                @if (history.field === 'marked_completed_date') {
                  <div class="update-history-item">
                    Marked {{ history.current }} by {{ actorName(history.created_by, history.actor_display) }} on
                    {{ history.created_date | dateTimeFromIso | dateTimeToFormat: 'MMM d, yyyy h:mm a' }}
                  </div>
                }
              }
            </div>
          }
        </div>
      }
    </form>
    <div mat-dialog-actions class="ee-modal-actions-split">
      <div class="ee-modal-actions-split-left">
        @if (data.editorType !== CaseTaskEditorType.WorkflowEdit) {
          <button mat-button (click)="delete()" color="accent">DELETE</button>
        }
      </div>
      <div class="ee-modal-actions">
        <button mat-button [mat-dialog-close]="null">CANCEL</button>
        <button mat-flat-button (click)="save()" cdkFocusInitial color="accent" [disabled]="form.invalid">
          {{ data.editorType === CaseTaskEditorType.WorkflowEdit ? 'APPLY' : 'SAVE' }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .update-history-wrapper {
        border-top: 1px solid #d7d7d7;
        padding-top: 0.5rem;

        .update-history-header {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;

          .update-history-title {
            font-weight: bold;
            margin-bottom: 0.5rem;
            text-align: center;
          }
        }

        .update-history-item {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: flex-start;
          font-size: 0.75rem;
          margin-bottom: 0.5rem;
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CaseTaskEditorDialogComponent implements OnDestroy {
  /** EE-531: support-aware actor naming for the template. */
  protected readonly actorName = actorName;

  form: UntypedFormGroup;
  title: string = 'Edit Task';
  subs: Subscription[] = [];
  CaseTaskEditorType = CaseTaskEditorType;
  showUpdateHistory = false;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CaseTaskEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      taskDetails: BaseTask | null;
      type: TaskType;
      editorType: CaseTaskEditorType;
      userSuggestions: User[];
    }
  ) {
    this.form = GenerateBaseTaskForm(this.fb, this.data.type, this.data.taskDetails);

    this.subs.push(
      this.form.get('required').valueChanges.subscribe((v) => {
        if (v) {
          this.form.get('remove_on_step_completion').setValue(false);
          this.form.get('remove_on_step_completion').disable();
        } else {
          this.form.get('remove_on_step_completion').enable();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  save() {
    this.dialogRef.close({ operation: CaseTaskOperation.Edit, form: this.form });
  }

  delete() {
    this.dialogRef.close({ operation: CaseTaskOperation.Delete });
  }

  protected readonly TaskType = TaskType;
}
